// src/components/AppUpdate/AppUpdate.jsx
//
// Una pestaña de Oddis que se queda abierta sigue corriendo el JS con el que se cargó,
// aunque ya se haya desplegado otro: una SPA nunca vuelve a pedir el HTML. Aquí se
// compara la versión embebida en el bundle (REACT_APP_VERSION, ver scripts/build.js)
// con /version.json, que Vercel sirve sin caché (ver vercel.json). Si no coinciden:
//   - en el siguiente cambio de ruta se hace una carga completa (el usuario ya se iba
//     de la pantalla, no pierde nada), y
//   - mientras tanto se muestra un aviso con el botón «Actualizar».
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Paper, Typography } from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

const CURRENT_VERSION = process.env.REACT_APP_VERSION;
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

async function fetchDeployedVersion() {
    try {
        const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return null;
        const data = await response.json();
        return typeof data.version === 'string' ? data.version : null;
    } catch {
        // Sin conexión: se reintenta en el siguiente ciclo.
        return null;
    }
}

const AppUpdate = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const location = useLocation();
    const firstPath = useRef(location.pathname);

    useEffect(() => {
        if (!CURRENT_VERSION) return undefined; // build local / `npm start`

        let checking = false;
        const check = async () => {
            if (checking) return;
            checking = true;
            const deployed = await fetchDeployedVersion();
            checking = false;
            if (deployed && deployed !== CURRENT_VERSION) setUpdateAvailable(true);
        };
        const onVisible = () => {
            if (document.visibilityState === 'visible') check();
        };

        check();
        const interval = window.setInterval(check, CHECK_INTERVAL_MS);
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', check);
        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', check);
        };
    }, []);

    // Con una versión nueva pendiente, el cambio de ruta se vuelve carga completa.
    useEffect(() => {
        if (!updateAvailable || location.pathname === firstPath.current) {
            firstPath.current = location.pathname;
            return;
        }
        window.location.reload();
    }, [location.pathname, updateAvailable]);

    if (!updateAvailable) return null;

    return (
        <Paper
            elevation={6}
            role="status"
            sx={{
                position: 'fixed',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                maxWidth: 'calc(100vw - 32px)',
            }}
        >
            <SystemUpdateAltIcon color="primary" />
            <Typography variant="body2">Hay una versión nueva de Oddis Maps.</Typography>
            <Button size="small" variant="contained" onClick={() => window.location.reload()}>
                Actualizar
            </Button>
        </Paper>
    );
};

export default AppUpdate;
